import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { GatewayAlert, gatewayStatus } from './api';
import { AuthProvider, useAuth } from './auth';
import { icons } from './components/Icons';
import { Rail } from './components/Rail';
import { Account } from './pages/Account';
import { AdminUser } from './pages/AdminUser';
import { AdminUsers } from './pages/AdminUsers';
import { Customer } from './pages/Customer';
import { Customers } from './pages/Customers';
import { Install } from './pages/Install';
import { Journeys } from './pages/Journeys';
import { LiveMap } from './pages/LiveMap';
import { Login } from './pages/Login';
import { Trackers } from './pages/Trackers';
import { UnitPage } from './pages/UnitPage';
import { Vehicles } from './pages/Vehicles';
import { Subscriptions } from './pages/Subscriptions';
import { Gateway } from './pages/Gateway';
import { GeofenceEdit } from './pages/GeofenceEdit';
import { Geofences } from './pages/Geofences';
import { TachoFile } from './pages/TachoFile';
import { TachoFiles } from './pages/TachoFiles';
import { TachoDriverPage, TachoDrivers } from './pages/TachoDrivers';
import './styles.css';

// Old vehicles-admin links lead to the unit page.
// The Gateway menu item shows a dot when the gateway needs attention (over 70 % of capacity,
// or not answering). Checked every 2 minutes.
function useGatewayAlert(loggedIn: boolean) {
  const [alert, setAlert] = useState<GatewayAlert | null>(null);
  useEffect(() => {
    if (!loggedIn) return;
    const check = () => gatewayStatus.alert().then(setAlert).catch(() => {});
    check();
    const t = setInterval(check, 120_000);
    return () => clearInterval(t);
  }, [loggedIn]);
  return alert;
}


function OldVehicleLink() {
  return <Navigate to={`/vehicles/${useParams().id}`} replace />;
}

function Shell() {
  const { user, checking, logout } = useAuth();
  const gw = useGatewayAlert(!!user && !user.mustChangePassword);
  if (checking) return <div className="center muted">Loading…</div>;
  if (!user) return <Login />;
  if (user.mustChangePassword) return <Account forced />;
  return (
    <div className="app">
      <Rail product="Sprout" user={user} onLogout={() => void logout()} items={[
        { to: '/', end: true, label: 'Live map', icon: icons.map, phone: true, match: (p) => p === '/' || /^\/vehicles\/\d+\/journeys/.test(p) || p === '/journeys' },
        { to: '/vehicles', label: 'Vehicles', icon: icons.truck, phone: true, match: (p) => p.startsWith('/vehicles') && !/\/journeys/.test(p) },
        { to: '/trackers', label: 'Trackers', icon: icons.tracker, phone: true },
        { to: '/customers', label: 'Customers', icon: icons.users, phone: true },
        { to: '/tacho', label: 'Tachograph', icon: icons.tacho },
        { to: '/subscriptions', label: 'Subscriptions', icon: icons.receipt },
        { to: '/gateway', label: 'Gateway', icon: icons.server,
          badge: gw && gw.level !== 'ok' ? <span className={`nav-dot ${gw.level}`} title={gw.message ?? undefined} aria-label={gw.message ?? 'needs attention'} /> : undefined },
        ...(user.role === 'admin' ? [{ to: '/admin/users', label: 'Users', icon: icons.shield }] : []),
      ]} />
      <main>
        <Routes>
          <Route path="/" element={<LiveMap />} />
          <Route path="/journeys" element={<Journeys />} />
          <Route path="/vehicles/:id/journeys" element={<Journeys />} />
          <Route path="/vehicles/:id/journeys/:start" element={<Journeys />} />
          <Route path="/vehicles" element={<Vehicles />} />
          <Route path="/vehicles/:id" element={<UnitPage from="vehicle" />} />
          <Route path="/trackers" element={<Trackers />} />
          <Route path="/trackers/:id" element={<UnitPage from="tracker" />} />
          <Route path="/install" element={<Install />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/subscriptions" element={<Subscriptions />} />
          <Route path="/gateway" element={<Gateway />} />
          <Route path="/customers/:id" element={<Customer />} />
          <Route path="/geofences" element={<Geofences />} />
          <Route path="/geofences/:id" element={<GeofenceEdit />} />
          <Route path="/tacho" element={<TachoFiles />} />
          <Route path="/tacho/drivers" element={<TachoDrivers />} />
          <Route path="/tacho/drivers/:id" element={<TachoDriverPage />} />
          <Route path="/tacho/:id" element={<TachoFile />} />
          <Route path="/admin/vehicles" element={<Navigate to="/vehicles" replace />} />
          <Route path="/admin/vehicles/:id" element={<OldVehicleLink />} />
          <Route path="/account" element={<Account />} />
          {user.role === 'admin' && <Route path="/admin/users" element={<AdminUsers />} />}
          {user.role === 'admin' && <Route path="/admin/users/:id" element={<AdminUser />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
